import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  TemplateRef,
} from '@angular/core';
import { ApplicationConfigurationDto, ConfigStateService, LocalizationService } from '@abp/ng.core';
import { Router } from '@angular/router';
import { NzAutocompleteOptionComponent } from 'ng-zorro-antd/auto-complete';
import { AppMenuDto } from '../../../app.model';
import { RoutesService } from '@abp/ng.core';
import { ShortcutInput } from 'ng-keyboard-shortcuts';
import { NzDrawerRef, NzDrawerService } from 'ng-zorro-antd/drawer';
import { ManageProfileComponent } from 'src/app/account/manage-profile/manage-profile.component';
import { SupportComponent } from '../../support/support/support.component';
import { SiteSiderComponent } from '../site-sider/site-sider.component';
import { NotificationsComponent } from '../../notifications/notifications.component';
import { environment } from 'src/environments/environment';
import { addToVisited, renderMenu, visibleMenus } from 'src/app/menu.provider';
import { OverdueNotificationService } from 'source/app/proxy/integration/overdue-alerts';
import { OverdueNotificationDto } from 'source/app/proxy/overdue-alerts';
import { UserProfileDto, UserProfileService } from 'source/app/proxy/users';
import { CreditTermBreachNotificationService } from 'src/app/proxy/credit-term-breach';
import { forkJoin } from 'rxjs';
import { AppImages } from 'source/resources/app_images';

@Component({
  selector: 'app-site-header',
  templateUrl: './site-header.component.html',
  styleUrls: ['./site-header.component.scss'],
})
export class SiteHeaderComponent implements OnInit {
  @ViewChild('menuDrawerTitle', { static: true })
  menuDrawerTitle!: TemplateRef<any>;

  primaryColor: string;
  secondaryColor: string;
  currentApplicationVersion = `v${environment.appVersion}`;

  menuDrawerRef?: NzDrawerRef;

  @Input() isCollapsed: boolean;
  @Output() isCollapsedChange = new EventEmitter<boolean>();

  @ViewChild('search') search: ElementRef;

  currentUser?: ApplicationConfigurationDto['currentUser'];

  searchText: string;

  allMenus: AppMenuDto[] = [];
  menus: AppMenuDto[] = [];

  shortcuts: ShortcutInput[] = [];
  isMobileAppInfoVisible: boolean = false;
  brandName = this.getBrandName();
  logoUrl = AppImages.focaliLogoGrey84Path;
  showLogo = false;

  enableMobile = localStorage.getItem('tenant_disableMobile') !== 'true';

  isNotificationDrawerVisible: boolean;

  allAlertsForCurrentUser: any[] = [];
  latestAlertsForDrawer: any[] = [];

  userProfile: UserProfileDto;

  constructor(
    private router: Router,
    private localizationService: LocalizationService,
    public routes: RoutesService,
    private nzDrawerService: NzDrawerService,
    private configState: ConfigStateService,
    private overdueNotificationService: OverdueNotificationService,
    private userProfileService: UserProfileService,
    private creditService: CreditTermBreachNotificationService,
  ) {
    this.configState.getAll$().subscribe(config => {
      if (!config?.currentUser?.isAuthenticated) {
        return;
      }
      this.currentUser = { ...config.currentUser };
      this.userProfileService.getCurrentUser().subscribe(profile => {
        this.userProfile = profile;
        // Deferred: this only feeds the notification bell badge/drawer, not any
        // page's visible content, so it doesn't need to compete with critical-path
        // requests for bandwidth/connection slots during initial page load.
        setTimeout(() => this.loadAllNotifications(), 2000);
      });
      this.updateBranding();
    });
  }

  ngOnInit(): void {
    this.primaryColor = environment.primaryColor;
    this.secondaryColor = environment.secondaryColor;
    this.shortcuts.push({
      key: 'ctrl + space',
      command: () => {
        this.search.nativeElement.focus();
      },
      preventDefault: true,
    });
    document.documentElement.style.setProperty('--header-color', this.secondaryColor);
  }

  private updateBranding() {
  this.brandName = this.getBrandName();
  this.showLogo = this.brandName?.toLowerCase() === 'focal-i';
}

  private getBrandName(): string {
    return localStorage.getItem('tenant_AppName') || localStorage.getItem('__tenant') || 'focal-i';
  }

  setIsCollapsed(value: boolean) {
    this.isCollapsed = value;
    this.isCollapsedChange.emit(this.isCollapsed);
  }

  onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (value) {
      this.menus = this.allMenus.filter(
        option => option.name.toLowerCase().indexOf(value.toLowerCase()) !== -1,
      );
    } else {
      this.populateLasVisited();
    }
  }

  onSelect(event: NzAutocompleteOptionComponent): void {
    this.setIsCollapsed(true);
    setTimeout(() => {
      this.searchText = '';
      this.menus = [];
    }, 1);

    const menu = event.nzValue as AppMenuDto;
    this.router.navigateByUrl(menu.path);
    addToVisited(menu.path);
  }

  onSelectNewWin(event: PointerEvent, menu: AppMenuDto) {
    event.preventDefault();
    event.stopImmediatePropagation();
    window.open(menu.path, '_blank');
    addToVisited(menu.path);
  }

  initSearch() {
    renderMenu(this.routes, this.localizationService);
    if (visibleMenus && visibleMenus.length > 0) {
      const allMenus = visibleMenus.map(m => {
        m.name = this.localizationService.instant(m.name);
        return m;
      });
      this.allMenus = allMenus;
    } else {
      console.warn('visibleMenus is still empty after renderMenu');
    }
    this.populateLasVisited();
  }

  private populateLasVisited() {
    if (!this.searchText) {
      const menuVisitedStr = localStorage.getItem('menuVisited');
      if (menuVisitedStr) {
        const menuVisited = JSON.parse(menuVisitedStr) as any[];
        this.menus = this.allMenus.filter(f => menuVisited.find(m => m.path === f.path));
      }
    }
  }

  profileClick() {
    this.nzDrawerService.create<ManageProfileComponent, any>({
      nzContent: ManageProfileComponent,
      nzBodyStyle: {
        backgroundColor: '#f2f2f3',
      },
      nzWidth: '40%',
      nzContentParams: {},
      nzCloseOnNavigation: false,
    });
  }

  supportClick() {
    this.nzDrawerService.create<SupportComponent, any>({
      nzTitle: 'Submit Ticket',
      nzContent: SupportComponent,
      nzMask: false,
      nzWidth: '40%',
      nzContentParams: {},
    });
  }

  openMenu() {
    this.menuDrawerRef = this.nzDrawerService.create({
      nzContent: SiteSiderComponent,
      nzWidth: window.innerWidth < 800 ? '75%' : '25%',
      nzPlacement: 'left',
      nzMaskClosable: true,
      nzClosable: false,
      nzTitle: this.menuDrawerTitle,
    });
  }

  notifications() {
    this.nzDrawerService.create<NotificationsComponent, any>({
      nzTitle: 'Notifications',
      nzContent: NotificationsComponent,
      nzWidth: window.innerWidth < 800 ? '75%' : '25%',
      nzNoAnimation: true,
      nzMaskClosable: true,
      nzPlacement: 'right',
      nzContentParams: {},
    });
  }
  mobileAppInfo() {
    this.isMobileAppInfoVisible = true;
  }
  loadAllNotifications() {
  if (!this.userProfile?.userId) return;

  forkJoin({
    overdue: this.overdueNotificationService.getNotificationHistory(this.userProfile.userId),
    credit: this.creditService.getNotificationHistory(this.userProfile.userId)
  }).subscribe(res => {

    const overdueAlerts = (res.overdue || []).filter(
      alert => alert.isSentMobile || alert.isSentDesktop || alert.isSentEmail
    );

    const creditAlerts = res.credit || [];

    this.allAlertsForCurrentUser = [...overdueAlerts, ...creditAlerts]
      .sort((a, b) =>
        new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime()
      );

    this.latestAlertsForDrawer = this.allAlertsForCurrentUser.slice(0, 10);
  });
}

  // openNotificationsDrawer() {
  //   const userId = this.userProfile.userId;
  //   this.overdueNotificationService.getNotificationHistory(userId).subscribe(res => {
  //     this.allAlertsForCurrentUser = res;
  //     if (this.allAlertsForCurrentUser.length > 0) {
  //       this.isNotificationDrawerVisible = true;
  //     }
  //   });
  // }

  openNotificationsDrawer() {
    this.loadAllNotifications();
    if (this.allAlertsForCurrentUser.length > 0) {
    this.isNotificationDrawerVisible = true;
    }
  }

  closeNotifications() {
    this.isNotificationDrawerVisible = false;
  }

  goToAllNotifications() {
    this.closeNotifications();
    this.router.navigate(['/notifications/overdue-alerts'], {
      state: { user: this.userProfile },
    });
  }

  removeAlert(alert: string) {}
  markAllAsRead() {}
}
